import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Users, Phone, Mail, MapPin, Building,
  MoreVertical, Edit, Trash2, X, MessageSquare, FileText,
  Mic, Square, Play, Pause, Send, Calendar, TrendingUp, Eye,
  UserCheck
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { GPSLocationPicker } from '../../../components/ui/gps-location-picker';
import { toast } from 'sonner';
import ClientFileModal from './ClientFileModal';

import { API_URL } from '../../../config/api';

const sourceOptions = [
  { value: 'website', label: 'Site web' },
  { value: 'phone', label: 'Téléphone' },
  { value: 'recommendation', label: 'Recommandation' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'flyer', label: 'Flyer/Pub' },
  { value: 'other', label: 'Autre' },
];

const chantierTypeOptions = [
  { value: 'renovation', label: 'Rénovation' },
  { value: 'construction', label: 'Construction' },
  { value: 'extension', label: 'Extension' },
  { value: 'plomberie', label: 'Plomberie' },
  { value: 'electricite', label: 'Électricité' },
  { value: 'peinture', label: 'Peinture' },
  { value: 'toiture', label: 'Toiture' },
  { value: 'carrelage', label: 'Carrelage' },
  { value: 'maconnerie', label: 'Maçonnerie' },
  { value: 'autre', label: 'Autre' },
];

const statusOptions = [
  { value: 'new', label: 'Nouveau', color: 'bg-blue-100 text-blue-600' },
  { value: 'contacted', label: 'Contacté', color: 'bg-purple-100 text-purple-600' },
  { value: 'meeting_scheduled', label: 'RDV pris', color: 'bg-amber-100 text-amber-600' },
  { value: 'quote_pending', label: 'Devis en cours', color: 'bg-orange-100 text-orange-600' },
  { value: 'quote_sent', label: 'Devis envoyé', color: 'bg-pink-100 text-pink-600' },
  { value: 'quote_accepted', label: 'Devis accepté', color: 'bg-green-100 text-green-600' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-600' },
];

export default function ProspectsPage() {
  const { user } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [newProspect, setNewProspect] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postal_code: '',
    chantier_type: 'autre',
    chantier_description: '',
    estimated_value: 0,
    source: 'other'
  });
  const [newNote, setNewNote] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Fiche client complète
  const [showClientFile, setShowClientFile] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const navigate = useNavigate();
  
  const openClientFile = (prospectId) => {
    setSelectedClientId(prospectId);
    setShowClientFile(true);
  };
  
  const openAbracadatchat = async (prospect) => {
    const token = localStorage.getItem('client_token');
    
    try {
      // Appeler l'API pour créer/ouvrir la conversation
      const response = await fetch(`${API_URL}/api/crm/client/${prospect.id}/open-chat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Fermer le modal et naviguer vers la messagerie
        setShowDetailModal(false);
        navigate('/cockpit-pro/messagerie', { 
          state: { 
            openConversationId: data.conversation_id,
            clientName: data.client_name
          } 
        });
      } else {
        toast.error('Erreur lors de l\'ouverture de la conversation');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      toast.error('Erreur de connexion');
    }
  };
  
  const convertToClient = async (prospect) => {
    const token = localStorage.getItem('client_token');
    
    try {
      const response = await fetch(`${API_URL}/api/crm/client/${prospect.id}/convert-to-client`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Mettre à jour localement
        setProspects(prev => prev.map(p => 
          p.id === prospect.id ? { ...p, status: 'client' } : p
        ));
        
        if (selectedProspect?.id === prospect.id) {
          setSelectedProspect({ ...selectedProspect, status: 'client' });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la conversion');
      }
    } catch (error) {
      console.error('Error converting:', error);
      toast.error('Erreur de connexion');
    }
  };

  // Données DÉMO pour le mode sans authentification (vide pour nouvel utilisateur)
  const DEMO_PROSPECTS = [];

  useEffect(() => {
    fetchProspects();
    if (searchParams.get('action') === 'new') {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  const fetchProspects = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO : Si pas de token, utiliser les données fictives
    if (!token) {
      setProspects(DEMO_PROSPECTS);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProspects(data);
      }
    } catch (error) {
      console.error('Error fetching prospects:', error);
      // En cas d'erreur, utiliser les données démo
      setProspects(DEMO_PROSPECTS);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProspect = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newProspect)
      });

      if (response.ok) {
        toast.success('Prospect créé avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchProspects();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateStatus = async (prospectId, newStatus) => {
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects/${prospectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchProspects();
        if (selectedProspect?.id === prospectId) {
          setSelectedProspect(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedProspect) return;
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(
        `${API_URL}/api/batiment/prospects/${selectedProspect.id}/notes?content=${encodeURIComponent(newNote)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Note ajoutée');
        setNewNote('');
        // Refresh prospect details
        const detailResponse = await fetch(`${API_URL}/api/batiment/prospects/${selectedProspect.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (detailResponse.ok) {
          const data = await detailResponse.json();
          setSelectedProspect(data);
        }
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la note");
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!confirm('Supprimer ce prospect ?')) return;
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects/${prospectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Prospect supprimé');
        setShowDetailModal(false);
        setSelectedProspect(null);
        fetchProspects();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleConvertToClient = async (prospectId) => {
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects/${prospectId}/convert-to-client`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Prospect converti en client');
        setShowDetailModal(false);
        fetchProspects();
      }
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await uploadVoiceNote(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => setRecordingDuration(prev => prev + 1), 1000);
    } catch (error) {
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const uploadVoiceNote = async (blob) => {
    if (!selectedProspect) return;
    const token = localStorage.getItem('client_token');

    try {
      // Upload audio file
      const formData = new FormData();
      formData.append('file', blob, 'voice_note.webm');
      formData.append('transcribe', 'true');

      const uploadResponse = await fetch(`${API_URL}/api/files/voice/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        
        // Add voice note to prospect
        const noteContent = uploadData.transcription || 'Note vocale';
        const response = await fetch(
          `${API_URL}/api/batiment/prospects/${selectedProspect.id}/notes?content=${encodeURIComponent(noteContent)}&is_voice=true&voice_url=${encodeURIComponent(uploadData.file_url)}&transcription=${encodeURIComponent(uploadData.transcription || '')}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (response.ok) {
          toast.success('Note vocale ajoutée');
          // Refresh
          const detailResponse = await fetch(`${API_URL}/api/batiment/prospects/${selectedProspect.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (detailResponse.ok) {
            setSelectedProspect(await detailResponse.json());
          }
        }
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    }
  };

  const resetForm = () => {
    setNewProspect({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      postal_code: '',
      chantier_type: 'autre',
      chantier_description: '',
      estimated_value: 0,
      source: 'other'
    });
  };

  const openDetailModal = async (prospect) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/batiment/prospects/${prospect.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSelectedProspect(await response.json());
      } else {
        // Fallback to local data if API fails
        setSelectedProspect(prospect);
      }
    } catch (error) {
      // Fallback to local data if network error
      setSelectedProspect(prospect);
    }
    // Always open the modal
    setShowDetailModal(true);
  };

  const getStatusInfo = (status) => statusOptions.find(s => s.value === status) || statusOptions[0];
  const getSourceLabel = (source) => sourceOptions.find(s => s.value === source)?.label || source;
  const getChantierLabel = (type) => chantierTypeOptions.find(t => t.value === type)?.label || type;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.phone?.includes(searchQuery);
    const matchesStatus = !filterStatus || p.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prospects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Prospects</h1>
          <p className="text-rm-text-secondary">Gérez tes opportunités commerciales</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-rm-primary hover:bg-rm-primary/90" data-testid="new-prospect-btn">
          <Plus className="w-5 h-5 mr-2" />
          Nouveau prospect
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
        >
          <option value="">Tous les statuts</option>
          {statusOptions.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Prospects List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        {filteredProspects.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-[#1E3A5F]/10 to-[#F97316]/10 rounded-full flex items-center justify-center">
              <Users className="w-10 h-10 text-[#1E3A5F]" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Aucun prospect pour l'instant</h3>
            <p className="text-sm text-gray-500 mb-4">Ajoute ton premier prospect pour commencer à suivre tes opportunités.</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-[#1E3A5F] hover:bg-[#2d4a6f]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un prospect
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredProspects.map((prospect) => {
              const statusInfo = getStatusInfo(prospect.status);
              return (
                <div
                  key={prospect.id}
                  onClick={() => openDetailModal(prospect)}
                  className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  data-testid={`prospect-${prospect.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-rm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-rm-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{prospect.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-rm-text-secondary">
                        {prospect.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {prospect.phone}
                          </span>
                        )}
                        {prospect.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {prospect.city}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {getChantierLabel(prospect.chantier_type)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-rm-text">{formatCurrency(prospect.estimated_value)}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <TrendingUp className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-500">{prospect.signature_score || 50}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-rm-text">Nouveau prospect</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateProspect} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Nom *</label>
                  <input
                    type="text"
                    required
                    value={newProspect.name}
                    onChange={(e) => setNewProspect({ ...newProspect, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={newProspect.phone}
                      onChange={(e) => setNewProspect({ ...newProspect, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Email</label>
                    <input
                      type="email"
                      value={newProspect.email}
                      onChange={(e) => setNewProspect({ ...newProspect, email: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="jean@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Adresse</label>
                  <input
                    type="text"
                    value={newProspect.address}
                    onChange={(e) => setNewProspect({ ...newProspect, address: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="14 rue des Acacias"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Ville</label>
                    <input
                      type="text"
                      value={newProspect.city}
                      onChange={(e) => setNewProspect({ ...newProspect, city: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="Paris"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Code postal</label>
                    <input
                      type="text"
                      value={newProspect.postal_code}
                      onChange={(e) => setNewProspect({ ...newProspect, postal_code: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      placeholder="75001"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Type de chantier</label>
                    <select
                      value={newProspect.chantier_type}
                      onChange={(e) => setNewProspect({ ...newProspect, chantier_type: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {chantierTypeOptions.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-rm-text mb-1">Source</label>
                    <select
                      value={newProspect.source}
                      onChange={(e) => setNewProspect({ ...newProspect, source: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    >
                      {sourceOptions.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Description du chantier</label>
                  <textarea
                    value={newProspect.chantier_description}
                    onChange={(e) => setNewProspect({ ...newProspect, chantier_description: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    rows={3}
                    placeholder="Description des travaux..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rm-text mb-1">Montant estimé (€)</label>
                  <input
                    type="number"
                    value={newProspect.estimated_value}
                    onChange={(e) => setNewProspect({ ...newProspect, estimated_value: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                    placeholder="5000"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                  <Button type="submit" className="bg-rm-primary">Créer le prospect</Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedProspect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-rm-text">{selectedProspect.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusInfo(selectedProspect.status).color}`}>
                        {getStatusInfo(selectedProspect.status).label}
                      </span>
                      <span className="text-sm text-rm-text-secondary">
                        Score: {selectedProspect.signature_score || 50}%
                      </span>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedProspect.phone && (
                    <a href={`tel:${selectedProspect.phone}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <Phone className="w-5 h-5 text-rm-primary" />
                      <span>{selectedProspect.phone}</span>
                    </a>
                  )}
                  {selectedProspect.email && (
                    <a href={`mailto:${selectedProspect.email}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <Mail className="w-5 h-5 text-rm-primary" />
                      <span className="truncate">{selectedProspect.email}</span>
                    </a>
                  )}
                  {selectedProspect.phone && (
                    <button 
                      onClick={() => openAbracadatchat(selectedProspect)}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg hover:from-orange-100 hover:to-amber-100 text-orange-600 border border-orange-200"
                      data-testid="abracadatchat-btn"
                    >
                      <MessageSquare className="w-5 h-5" />
                      <span className="font-medium">Abracadatchat</span>
                    </button>
                  )}
                  <Link to={`/espace-client/crm-batiment/devis?action=new&prospect=${selectedProspect.id}`} className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg hover:bg-amber-100 text-amber-700">
                    <FileText className="w-5 h-5" />
                    <span>Créer devis</span>
                  </Link>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openClientFile(selectedProspect.id);
                    }}
                    className="flex items-center gap-3 p-3 bg-[#1E3A5F]/10 rounded-lg hover:bg-[#1E3A5F]/20 text-[#1E3A5F]"
                  >
                    <Eye className="w-5 h-5" />
                    <span>Fiche complète</span>
                  </button>
                  <button
                    onClick={() => {
                      toast.success('Compte-rendu envoyé à ' + selectedProspect.email);
                      // TODO: Implémenter l'envoi réel via Resend
                    }}
                    className="flex items-center gap-3 p-3 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg hover:from-cyan-100 hover:to-blue-100 text-cyan-700 border border-cyan-200"
                    data-testid="send-summary-btn"
                  >
                    <Send className="w-5 h-5" />
                    <span>Envoyer compte-rendu</span>
                  </button>
                  
                  {/* Bouton Convertir en Client - visible seulement si c'est un prospect */}
                  {selectedProspect.status !== 'client' && (
                    <button
                      onClick={() => convertToClient(selectedProspect)}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg hover:from-green-100 hover:to-emerald-100 text-green-700 border border-green-200 col-span-2"
                      data-testid="convert-to-client-btn"
                    >
                      <UserCheck className="w-5 h-5" />
                      <span className="font-medium">Convertir en Client</span>
                    </button>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-rm-text">Détails</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-rm-text-secondary">Type de chantier</span>
                      <p className="font-medium">{getChantierLabel(selectedProspect.chantier_type)}</p>
                    </div>
                    <div>
                      <span className="text-rm-text-secondary">Montant estimé</span>
                      <p className="font-medium">{formatCurrency(selectedProspect.estimated_value)}</p>
                    </div>
                    <div>
                      <span className="text-rm-text-secondary">Source</span>
                      <p className="font-medium">{getSourceLabel(selectedProspect.source)}</p>
                    </div>
                    <div>
                      <span className="text-rm-text-secondary">Créé le</span>
                      <p className="font-medium">{formatDate(selectedProspect.created_at)}</p>
                    </div>
                  </div>
                  {selectedProspect.address && (
                    <div>
                      <span className="text-rm-text-secondary text-sm">Adresse</span>
                      <p className="font-medium">{selectedProspect.address}, {selectedProspect.postal_code} {selectedProspect.city}</p>
                    </div>
                  )}
                  {selectedProspect.chantier_description && (
                    <div>
                      <span className="text-rm-text-secondary text-sm">Description</span>
                      <p className="text-sm mt-1">{selectedProspect.chantier_description}</p>
                    </div>
                  )}
                </div>

                {/* GPS Location for Pointage */}
                <GPSLocationPicker
                  targetType="prospect"
                  targetId={selectedProspect.id}
                  currentLatitude={selectedProspect.latitude}
                  currentLongitude={selectedProspect.longitude}
                  onUpdate={(coords) => {
                    setSelectedProspect(prev => ({ ...prev, ...coords }));
                    setProspects(prev => prev.map(p => 
                      p.id === selectedProspect.id ? { ...p, ...coords } : p
                    ));
                  }}
                />

                {/* Status Change */}
                <div>
                  <h4 className="font-semibold text-rm-text mb-2">Changer le statut</h4>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map(s => (
                      <button
                        key={s.value}
                        onClick={() => handleUpdateStatus(selectedProspect.id, s.value)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                          selectedProspect.status === s.value ? s.color : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h4 className="font-semibold text-rm-text mb-3">Notes</h4>
                  
                  {/* Add Note */}
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Ajouter une note..."
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    {isRecording ? (
                      <Button onClick={stopRecording} variant="destructive" size="icon">
                        <Square className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button onClick={startRecording} variant="outline" size="icon" title="Note vocale">
                        <Mic className="w-4 h-4" />
                      </Button>
                    )}
                    <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>

                  {isRecording && (
                    <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 rounded-lg">
                      <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-sm text-red-600">Enregistrement: {recordingDuration}s</span>
                    </div>
                  )}

                  {/* Notes List */}
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {selectedProspect.notes?.length === 0 && (
                      <p className="text-sm text-rm-text-secondary text-center py-4">Aucune note</p>
                    )}
                    {selectedProspect.notes?.map((note) => (
                      <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {note.is_voice && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full mr-2">Vocale</span>
                            )}
                            <p className="text-sm">{note.content}</p>
                            {note.transcription && note.transcription !== note.content && (
                              <p className="text-xs text-slate-500 mt-1 italic">"{note.transcription}"</p>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          {note.created_by_name} • {formatDate(note.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-100 flex justify-between">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleDeleteProspect(selectedProspect.id)} className="text-red-500 hover:text-red-600">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleConvertToClient(selectedProspect.id)}>
                    Convertir en client
                  </Button>
                  <Button onClick={() => setShowDetailModal(false)}>Fermer</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal Fiche Client Complète */}
      <ClientFileModal
        clientId={selectedClientId}
        isOpen={showClientFile}
        onClose={() => setShowClientFile(false)}
      />
    </div>
  );
}
